import { Component, EventEmitter, Input, OnChanges, Output } from "@angular/core";
import { Connection } from "@common/ADAPT.Common.Model/organisation/connection";
import { HandoverStatus, HandoverStatusMetadata } from "@common/ADAPT.Common.Model/organisation/handover-status";
import { Role } from "@common/ADAPT.Common.Model/organisation/role";
import { RoleConnection } from "@common/ADAPT.Common.Model/organisation/role-connection";
import { CommonDataService } from "@common/lib/data/common-data.service";
import { RxjsBreezeService } from "@common/lib/data/rxjs-breeze.service";
import { AdaptCommonDialogService } from "@common/ux/adapt-common-dialog/adapt-common-dialog.service";
import { BaseComponent } from "@common/ux/base.component/base.component";
import { IAdaptMenuItem, MenuComponent } from "@common/ux/menu/menu.component";
import moment from "moment";
import { EMPTY, filter, of } from "rxjs";
import { catchError, switchMap, tap } from "rxjs/operators";

@Component({
    selector: "adapt-team-roles-connection",
    standalone: false,
    templateUrl: "./team-roles-connection.component.html",
})
export class TeamRolesConnectionComponent extends BaseComponent implements OnChanges {
    @Input() public isEditing = false;
    @Input({ required: true }) public role!: Role;
    @Input({ required: true }) public connection!: Connection;

    @Output() public roleConnectionChanged = new EventEmitter<RoleConnection>();

    public roleConnection?: RoleConnection;
    public menuItems: IAdaptMenuItem[] = [];

    public constructor(
        private commonDataService: CommonDataService,
        private commonDialogService: AdaptCommonDialogService,
        rxjsBreezeService: RxjsBreezeService,
    ) {
        super();

        rxjsBreezeService.entityTypeChanged(RoleConnection).pipe(
            filter((rc) => rc.connectionId === this.connection.connectionId || rc.roleId == this.role.roleId),
            tap(() => this.buildMenu()),
            this.takeUntilDestroyed(),
        ).subscribe();
    }

    public ngOnChanges() {
        this.roleConnection = this.role.roleConnections.find((rc) => rc.connectionId === this.connection.connectionId && rc.isActive());

        this.buildMenu();
    }

    private buildMenu() {
        if (!this.roleConnection) {
            this.menuItems = [];
            return;
        }

        const setHandoverStatusMenuItems: IAdaptMenuItem[] = HandoverStatusMetadata.CurrentRoleHandoverOptions.map((handoverStatus) => ({
            text: handoverStatus.name,
            icon: handoverStatus.icon,
            // this can be null instead of undefined due to breeze, so just coalesce it to undefined
            isChecked: (this.roleConnection!.handoverStatus ?? undefined) === (handoverStatus.status ?? undefined),
            onIsCheckedChange: (isChecked) => this.changeHandoverStatus(isChecked ? handoverStatus.status : undefined).subscribe(),
        }));

        const setHandoverStatusMenu: IAdaptMenuItem = {
            text: "Set handover status...",
            icon: "fal fa-fw fa-hand-holding-box",
            items: setHandoverStatusMenuItems,
        };

        const removePersonFromRoleItem: IAdaptMenuItem = {
            text: "Remove person from role",
            icon: "fal fa-fw fa-trash",
            onClick: () => this.deleteOrEndRoleConnection().subscribe(),
            beginGroup: true,
        };

        this.menuItems = [{
            icon: MenuComponent.SmallRootMenu.icon,
            padding: false,
            items: [
                setHandoverStatusMenu,
                ...(!this.role.extensions.isSystemAllocatedRole() ? [removePersonFromRoleItem] : []),
            ],
        }];
    }

    private changeHandoverStatus(newStatus?: HandoverStatus) {
        if (!this.roleConnection) {
            return EMPTY;
        }

        this.roleConnection.handoverStatus = newStatus ?? undefined;

        return this.commonDataService.saveEntities([this.roleConnection]).pipe(
            catchError(() => this.commonDialogService.handleSaveFailure([this.roleConnection!])),
            tap(() => this.onChange()),
        );
    }

    private deleteOrEndRoleConnection() {
        if (!this.roleConnection) {
            return EMPTY;
        }

        const shouldDelete = moment().diff(this.roleConnection.startDate, "days") < 7;

        return this.commonDialogService.openConfirmationDialog({
            title: "End role for person",
            message: `<p>Are you sure you want to remove <b>${this.roleConnection?.connection?.person.fullName}</b> from the <b>${this.roleConnection?.role?.label}</b> role?</p>
                <p>You can add this person to this role again at any time.</p>`,
            confirmButtonPreset: "deleteAndSave",
        }).pipe(
            switchMap(() => {
                if (!this.roleConnection) {
                    return EMPTY;
                }

                if (shouldDelete) {
                    return this.commonDataService.remove(this.roleConnection);
                }

                this.roleConnection.endDate = moment.utc().toDate();
                if (this.roleConnection.endDate < this.roleConnection.startDate) {
                    this.roleConnection.endDate = this.roleConnection.startDate;
                }

                return of(this.roleConnection);
            }),
            switchMap(() => this.commonDataService.saveEntities([this.roleConnection!])),
            catchError(() => this.commonDialogService.handleSaveFailure([this.roleConnection!])),
            tap(() => this.onChange()),
        );
    }

    private onChange() {
        this.buildMenu();
        this.roleConnectionChanged.emit(this.roleConnection);
    }
}
